import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  Fragment,
} from 'react';
import { FaCalendar, FaCalendarDay, FaLocationArrow } from 'react-icons/fa';

import {
  Box,
  Text,
  Flex,
  Container,
  Button,
  Image,
  useToast,
  Img,
} from '@chakra-ui/react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { Personal, Company } from '~/@types/Survey';
import ipacTalks from '~/assets/IPAC-talks.png';
import logoImg from '~/assets/logo-form.png';
import InputChakra from '~/shared/components/InputChakra';
import SelectChakra from '~/shared/components/InputChakra/SelectChakra';
import LoadingAbsolute from '~/shared/components/LoadingAbsolute';
import api from '~/shared/services/api';
import { getValidationErrorWithFocus } from '~/utils/getValidationErrors';

interface SurveyOptions {
  id: number;
  name: string;
  label: string;
  value: string;
  visible?: boolean;
}
interface DataForm {
  name: string;
  email: string;
  phone: string;
  company_name: string;
  branch_activity: string;
  tributary_regime_id: string;
  responsibility_id: string;
}

interface Survey {
  tributary_regimes: SurveyOptions[];
  responsibilities: SurveyOptions[];
}

const ImmersionTaxReform: React.FC = () => {
  const addToast = useToast();
  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [tributaryRegime, setTributaryRegime] = useState<SurveyOptions[]>([]);
  const [responsibility, setResponsibility] = useState<SurveyOptions[]>([]);

  useEffect(() => {
    setLoading(true);
    api
      .get<Survey>(`/survey/filters`)
      .then((response) => {
        const { tributary_regimes, responsibilities } = response.data;

        setTributaryRegime(
          tributary_regimes.map((tr) => {
            return { ...tr, label: tr.name, value: tr.id.toString() };
          }),
        );
        setResponsibility(
          responsibilities.map((r) => {
            return { ...r, label: r.name, value: r.id.toString() };
          }),
        );
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = useCallback(
    async (dataSubmit: DataForm, { reset }) => {
      setLoadingSubmit(true);
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
          email: Yup.string()
            .required('E-mail obrigatório')
            .email('Digite um e-mail válido'),
          phone: Yup.string().required('Telefone obrigatório'),

          company_name: Yup.string().required('Nome da empresa obrigatório'),
          branch_activity: Yup.string().required(
            'Ramo da atividade obrigatório',
          ),
          tributary_regime_id: Yup.string().required(
            'Regime tributário obrigatório',
          ),
          responsibility_id: Yup.string().required('Cargo é obrigatório'),
        });

        await schema.validate(dataSubmit, {
          abortEarly: false,
        });

        await api.post<Survey>(`/survey/immersion-tax-reform`, dataSubmit);

        reset();

        addToast({
          position: 'top-right',
          isClosable: true,
          title: 'Formulário enviado!',
          description: `Formulário eviado com sucesso`,
          status: 'success',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrorWithFocus(err);
          formRef.current?.setErrors(errors);

          return;
        }

        addToast({
          position: 'top-right',
          isClosable: true,
          title: 'Erro ao tentar enviar formulário',
          description: '',
          status: 'error',
        });
      } finally {
        setLoadingSubmit(false);
      }
    },
    [addToast],
  );

  return (
    <Container pos="relative">
      {loading && <LoadingAbsolute z_index={1111} min_height={500} />}
      {!loading && (
        <>
          <Box marginTop="20px">
            <Image
              width="100%"
              objectFit="contain"
              src={logoImg}
              alt="MyIPAC"
              mb={10}
            />
          </Box>

          <Flex flexWrap="nowrap" maxWidth="100%">
            <Box pos="relative">
              <Img
                src={ipacTalks}
                alt="Flyer Ipac Talks"
                width="400px"
                marginRight="20px"
              />
            </Box>
            <Box marginLeft="15px">
              <Text
                as="strong"
                color="rgb(55, 71, 79)"
                fontSize="2xl"
                sx={{
                  '@media (max-width: 490px)': {
                    fontSize: 'xl',
                  },

                  '@media (max-width: 390px)': {
                    fontSize: 'lg',
                  },
                  '@media (max-width: 350px)': {
                    fontSize: 'md',
                  },
                }}
              >
                IPAC Talks: Impostos e Distribuição de Lucros: O Quê Muda com a
                Reforma Tributária?
              </Text>

              <Text color="rgb(55, 71, 79)">Produto</Text>
              <Text color="#38A169" as="strong" fontSize="1.25rem">
                Gratuito
              </Text>
            </Box>
          </Flex>

          <Flex
            marginBottom="20px"
            marginTop="20px"
            gap={4}
            direction="row"
            sx={{
              '@media (max-width: 370px)': {
                flexDirection: 'column',
              },
            }}
          >
            <Box
              as="section"
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="md"
              p={5}
              w="100%"
              transition="box-shadow 0.2s"
              _hover={{ boxShadow: 'md' }}
              sx={{
                '@media (max-width: 370px)': {
                  marginBottom: '10px',
                },
              }}
              marginRight="15px"
            >
              <Flex align="center" mb={4} gap={2}>
                <FaCalendarDay color="#2a4365" width={5} height={5} />
                <Text
                  fontWeight="700"
                  color="blue.800"
                  lineHeight="short"
                  marginLeft="10px"
                  fontSize="lg"
                >
                  Data do evento
                </Text>
              </Flex>

              <Flex
                direction="column"
                gap={4}
                sx={{
                  '@media (min-width: 450px)': {
                    flexDirection: 'row',
                  },
                }}
              >
                <Box
                  color="gray.600"
                  fontSize="sm"
                  sx={{ '@media (min-width: 450px)': { marginBottom: '20px' } }}
                >
                  <Text color="#2a4365" as="strong">
                    Início
                  </Text>
                  <Text color="gray.600">04/12/2025, 15:00</Text>
                </Box>

                <Box color="gray.600" fontSize="sm">
                  <Text color="#2a4365" as="strong">
                    Término
                  </Text>
                  <Text color="gray.600">04/12/2025, 19:00</Text>
                </Box>
              </Flex>
            </Box>

            <Box
              as="section"
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              boxShadow="md"
              p={5}
              w="100%"
              transition="box-shadow 0.2s"
              _hover={{ boxShadow: 'md' }}
            >
              <Flex align="center" mb={4} gap={2}>
                <FaLocationArrow color="#2a4365" width={5} height={5} />
                <Text
                  fontWeight="700"
                  color="blue.800"
                  lineHeight="short"
                  marginLeft="10px"
                  fontSize="lg"
                >
                  Local
                </Text>
              </Flex>

              <Box color="gray.600" fontSize="sm">
                QD 2 lotes 420/440, SIG city offices lojas 39-44 Ed, Brasília –
                DF, 70610-420
              </Box>
              <Box color="gray.600" fontSize="sm">
                Espaço BRL
              </Box>
            </Box>
          </Flex>

          <Form ref={formRef} onSubmit={handleSubmit}>
            <Box>
              <Container
                backgroundColor="#fff"
                border="1px solid rgb(218,220,224)"
                borderRadius="8px"
                position="relative"
                paddingTop="22px"
                padding="24px"
                marginBottom="12px"
                marginTop="12px"
              >
                <Flex justifyContent="center">
                  <Text marginBottom="10px" fontSize="4xl">
                    Dados Pessoais
                  </Text>
                </Flex>

                <InputChakra name="survey_id" type="hidden" defaultValue={4} />

                <InputChakra
                  name="name"
                  label="Nome"
                  background="#e9ecef"
                  borderColor=""
                  mb="0px"
                />

                <InputChakra
                  name="email"
                  label="E-mail"
                  background="#e9ecef"
                  borderColor=""
                  mb="0px"
                />

                <InputChakra
                  name="phone"
                  label="Telefone"
                  background="#e9ecef"
                  borderColor=""
                  mb="0px"
                  mask="phone"
                />
              </Container>
            </Box>
            <Box>
              <>
                <Container
                  backgroundColor="#fff"
                  border="1px solid rgb(218,220,224)"
                  borderRadius="8px"
                  position="relative"
                  paddingTop="22px"
                  padding="24px"
                  marginBottom="12px"
                  marginTop="12px"
                >
                  <Flex justifyContent="center">
                    <Text fontSize="4xl" marginBottom="10px">
                      Empresas
                    </Text>
                  </Flex>
                  <InputChakra
                    name="company_name"
                    label="Nome da Empresa"
                    background="#e9ecef"
                    borderColor=""
                    mb="0px"
                  />
                  <InputChakra
                    name="branch_activity"
                    label="Ramo de Atividade"
                    background="#e9ecef"
                    borderColor=""
                    mb="0px"
                  />
                  <SelectChakra
                    name="tributary_regime_id"
                    label="Regime Tributário"
                    background="#e9ecef"
                    borderColor=""
                    mb="0px"
                    options={tributaryRegime}
                    placeholder="Selecione"
                  />
                  <SelectChakra
                    name="responsibility_id"
                    label="Cargo"
                    background="#e9ecef"
                    borderColor=""
                    mb="0px"
                    options={responsibility}
                    placeholder="Selecione"
                  />
                </Container>
              </>

              <Flex justifyContent="center">
                <Button
                  m={4}
                  colorScheme="blue"
                  width="150px"
                  height="50px"
                  onClick={() => formRef.current?.submitForm()}
                  isLoading={loadingSubmit}
                >
                  Se Inscrever
                </Button>
              </Flex>
            </Box>
          </Form>
        </>
      )}
    </Container>
  );
};

export default ImmersionTaxReform;
